import {
  ArrowDown,
  ArrowUpRight,
  Church,
  Flame,
  Globe2,
  Sprout,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

const pillars = [
  {
    number: "01",
    title: "Evangelismo",
    description: "Anunciar el Evangelio con claridad para que cada persona pueda encontrarse con Jesús.",
    icon: Globe2,
  },
  {
    number: "02",
    title: "Discipulado",
    description: "Formar seguidores de Cristo que vivan, compartan y multipliquen su fe.",
    icon: Flame,
  },
  {
    number: "03",
    title: "Plantación",
    description: "Establecer iglesias saludables que sirvan a sus comunidades y alcancen nuevas generaciones.",
    icon: Sprout,
  },
];

function Mm33Mark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`acad-mm33-mark${compact ? " acad-mm33-mark--compact" : ""}`} aria-label="MM33">
      MM<span>33</span>
    </span>
  );
}

export default function Mm33Page() {
  return (
    <div className="acad-react-page acad-mm33-page">
      <Header />

      <main>
        <section className="acad-mm33-hero" data-nav="dark">
          <img
            className="acad-mm33-hero-image"
            src={assetUrl("missions_crowd_worship.png")}
            alt="Congregación reunida en adoración"
            loading="eager"
            decoding="async"
          />
          <span className="acad-mm33-hero-shade" />
          <span className="acad-mm33-hero-grid" aria-hidden="true" />

          <div className="acad-mm33-shell acad-mm33-hero-inner">
            <div className="acad-mm33-hero-topline">
              <Mm33Mark compact />
              <span>Meta mundial · 2033</span>
            </div>

            <div className="acad-mm33-hero-copy">
              <span className="acad-mm33-eyebrow">2000 años de Pentecostés</span>
              <h1>
                Un millón
                <span>de iglesias</span>
                al <em>2033</em>
              </h1>
              <p>Su mandato. Nuestra misión.</p>
            </div>

            <div className="acad-mm33-hero-bottom">
              <a className="acad-mm33-scroll" href="#mision">
                <span>Descubrir la visión</span>
                <i><ArrowDown aria-hidden="true" /></i>
              </a>
              <p>
                Una visión colaborativa para evangelizar, discipular y plantar iglesias
                saludables alrededor del mundo.
              </p>
            </div>
          </div>
        </section>

        <div className="acad-mm33-marquee" aria-label="Pilares de MM33">
          <div>
            <span>Evangelismo</span><i />
            <span>Discipulado</span><i />
            <span>Plantación de iglesias</span><i />
            <span>Evangelismo</span><i />
            <span>Discipulado</span><i />
            <span>Plantación de iglesias</span><i />
          </div>
        </div>

        <section className="acad-mm33-mission" id="mision">
          <div className="acad-mm33-shell acad-mm33-mission-grid">
            <div className="acad-mm33-mission-media">
              <img
                src={assetUrl("ag_world_missions_valley.png")}
                alt="Equipo misionero contemplando el territorio por alcanzar"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-mm33-image-label">Una misión global</span>
              <span className="acad-mm33-image-number">33</span>
            </div>

            <div className="acad-mm33-mission-copy">
              <span className="acad-mm33-eyebrow">Su mandato · Nuestra misión</span>
              <h2>Una visión que nos mueve juntos.</h2>
              <p>
                MM33 representa el esfuerzo colaborativo de las Asambleas de Dios Mundial
                para participar en la mayor obra de evangelismo, discipulado y plantación
                de iglesias que el mundo haya visto.
              </p>
              <p>
                La Gran Comisión es Su mandato y nuestra misión. Buscamos al Señor y damos
                pasos para crecer hasta un millón de iglesias para el aniversario número
                2000 de la resurrección de Jesucristo y el Día de Pentecostés en 2033.
              </p>
              <a className="acad-mm33-text-link" href="#compromiso">
                Conocer el compromiso <ArrowDown aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="acad-mm33-pillars">
          <div className="acad-mm33-shell">
            <header className="acad-mm33-section-head">
              <span className="acad-mm33-eyebrow">La misión en acción</span>
              <h2>Tres movimientos.<br />Una sola visión.</h2>
            </header>

            <div className="acad-mm33-pillar-grid">
              {pillars.map((pillar) => {
                const Icon = pillar.icon;

                return (
                  <article className="acad-mm33-pillar" key={pillar.number}>
                    <div className="acad-mm33-pillar-top">
                      <span>{pillar.number}</span>
                      <i><Icon aria-hidden="true" /></i>
                    </div>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-mm33-history">
          <div className="acad-mm33-history-backdrop" aria-hidden="true">
            <Mm33Mark />
          </div>
          <div className="acad-mm33-shell acad-mm33-history-inner">
            <div className="acad-mm33-history-title">
              <span className="acad-mm33-eyebrow">Nuestra historia</span>
              <span className="acad-mm33-history-year">2017</span>
            </div>
            <div className="acad-mm33-history-copy">
              <h2>Una exhortación profética encendió una visión global.</h2>
              <p>
                Las Asambleas de Dios tienen una historia vibrante compartiendo el Evangelio
                a nivel mundial. En el Congreso Mundial de 2017 recibimos el desafío de pedir
                al Señor de la cosecha que, si tarda en su regreso hasta 2033, nuestra comunión
                esté compuesta por un millón de iglesias.
              </p>
              <p>
                Seguimos buscando Su guía, construyendo Su Iglesia y fomentando el avivamiento
                entre las naciones.
              </p>
            </div>
          </div>
        </section>

        <section className="acad-mm33-commitment" id="compromiso">
          <div className="acad-mm33-shell acad-mm33-commitment-grid">
            <div className="acad-mm33-commitment-copy">
              <span className="acad-mm33-eyebrow">El compromiso</span>
              <h2>Avanzamos en unidad.</h2>
              <p>
                El compromiso representa el acuerdo compartido de participar activamente en
                la plantación de iglesias, el evangelismo y el discipulado.
              </p>
              <p>
                Los líderes que se suman afirman la visión colaborativa de MM33 y deciden
                avanzar juntos para expandir el Reino.
              </p>
              <a className="acad-mm33-button" href={homeUrl("contacto/?tema=mm33#contacto-formulario")}>
                Sumarme a MM33 <ArrowUpRight aria-hidden="true" />
              </a>
            </div>

            <div className="acad-mm33-commitment-media">
              <img
                src={assetUrl("church_multiplication_network.png")}
                alt="Líderes unidos en oración y compromiso"
                loading="lazy"
                decoding="async"
              />
              <div className="acad-mm33-commitment-badge">
                <Church aria-hidden="true" />
                <span>Iglesias saludables que se multiplican</span>
              </div>
            </div>
          </div>
        </section>

        <section className="acad-mm33-stat">
          <div className="acad-mm33-shell acad-mm33-stat-inner">
            <span className="acad-mm33-stat-kicker">La visión ya está en movimiento</span>
            <strong>390,375</strong>
            <div className="acad-mm33-stat-bottom">
              <h2>Iglesias han sido plantadas</h2>
              <p>Hacia la meta global de multiplicación al 2033.</p>
            </div>
          </div>
        </section>

        <section className="acad-mm33-final">
          <div className="acad-mm33-shell acad-mm33-final-inner">
            <Mm33Mark />
            <div>
              <span>La misión continúa</span>
              <h2>Hay un lugar para ti en esta historia.</h2>
            </div>
            <a href={homeUrl("contacto/?tema=mm33#contacto-formulario")} aria-label="Contactar a ACAD sobre MM33">
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
